/**
 * Profiles provider.
 *
 * Seeds the Profile **list** from the session bootstrap payload
 * (so the gear-menu Profiles submenu renders Global vs User groups
 * on first paint without a second round-trip — REQ-AUTH-065) and
 * supplies the read-side helpers the menu calls: :func:`refresh`,
 * :func:`getDocument`, :func:`exportProfile`.
 *
 * Mounted by :class:`SessionProvider` once the bootstrap response
 * is available (mirroring :class:`PreferencesProvider`), so the
 * initial list is already in hand and the mount is synchronous.
 *
 * Increment 4a wires the read slice only; the write side
 * (applyProfile / save / update — ADR-033 §4.2) lands in later
 * increments and belongs to the preferences working-state writer.
 *
 * Cross-references:
 *   - REQ-AUTH-065 (bootstrap + list shape).
 *   - REQ-AUTH-066 (single-Profile read).
 *   - REQ-UI-074 (Profiles submenu).
 *   - REQ-UI-081 (export).
 *   - REQ-UI-037 (generic user-facing error copy).
 *   - ADR-033 §4.2.
 */
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useSession } from "@/auth/session-context";
import type { ProfileDetail, ProfileSummary } from "@/auth/session-types";
import { ApiError } from "@/lib/api-client";
import { getProfile, listProfiles } from "@/profiles/profiles-client";
import {
  ProfilesContext,
  type ExportProfileResult,
  type ProfilesContextValue,
} from "@/profiles/profiles-context";
import {
  buildProfileExport,
  downloadJson,
  profileExportFilename,
} from "@/profiles/profiles-export";

interface ProfilesProviderProps {
  /** Bootstrap-payload Profiles list — seeds the initial state.
   *  Optional so a bootstrap that predates the field degrades to
   *  an empty list (the menu then shows the empty groups and a
   *  ``refresh()`` repopulates). */
  initial?: readonly ProfileSummary[];
  children: ReactNode;
}

/**
 * The provider. Owns the in-memory Profile list; document fetches
 * are on-demand (never cached here — REQ-UI-026).
 */
export function ProfilesProvider({ initial, children }: ProfilesProviderProps): React.ReactElement {
  const session = useSession();
  const [profiles, setProfiles] = useState<readonly ProfileSummary[]>(() => initial ?? []);

  const refresh = useCallback(async (): Promise<void> => {
    // Let the caller decide how to surface a failed refresh; the
    // list simply stays at its last-known value on throw. We don't
    // swallow the error here so a future caller can react, but we
    // also don't crash the menu on a transient blip — callers in
    // 4a (none yet) and later increments wrap as needed.
    const next = await listProfiles();
    setProfiles(next);
  }, []);

  const getDocument = useCallback(
    (profileId: string): Promise<ProfileDetail> => getProfile(profileId),
    [],
  );

  const exportProfile = useCallback(
    async (profileId: string): Promise<ExportProfileResult> => {
      try {
        const detail = await getProfile(profileId);
        const wrapper = buildProfileExport(detail, {
          sourceNetwork: session.network,
          exportedAt: new Date().toISOString(),
        });
        downloadJson(profileExportFilename(detail.name), wrapper);
        return { status: "ok" };
      } catch (error) {
        return { status: "error", message: describeExportError(error) };
      }
    },
    [session.network],
  );

  const value = useMemo<ProfilesContextValue>(
    () => ({ profiles, refresh, getDocument, exportProfile }),
    [profiles, refresh, getDocument, exportProfile],
  );

  return <ProfilesContext.Provider value={value}>{children}</ProfilesContext.Provider>;
}

/**
 * Coerce a thrown value into a generic, user-facing export-failure
 * message per REQ-UI-037 — never leak the BFF's internal error
 * body. A 404 means the Profile vanished (or the caller lost
 * access) between list and export; everything else is a generic
 * retry.
 */
function describeExportError(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "That profile is no longer available.";
  }
  return "Couldn't export this profile — please try again.";
}
